import { ActionIcon, Group, Popover, Radio, Slider, Text } from "@mantine/core";
import { LayoutGrid, Rows3, Settings2 } from "lucide-react";

import classes from "./DisplaySettings.module.css";

/** A gallery's layout (spec library-rows): live's masonry grid, or its rows. */
export type GalleryLayout = "masonry" | "rows";

/** Live's column counts (`gridColumnCountMarks`). */
export const COLUMN_MARKS = [2, 4, 6, 8, 10] as const;

/** Live's row heights (`rowHeightMarks`). */
export const ROW_HEIGHTS = [
  { value: 150, label: "Small" },
  { value: 200, label: "Medium" },
  { value: 250, label: "Large" },
] as const;

/** The nearest of live's column counts: one kept from the slider of 2 to 12 before these marks. */
export function columnMark(columns: number): number {
  return COLUMN_MARKS.reduce((near, mark) => (Math.abs(mark - columns) < Math.abs(near - columns) ? mark : near));
}

const LAYOUTS = [
  { value: "masonry", label: "Grid", Icon: LayoutGrid },
  { value: "rows", label: "Rows", Icon: Rows3 },
] as const;

export interface DisplaySettingsProps {
  layout: GalleryLayout;
  onLayout: (layout: GalleryLayout) => void;
  /** The grid's column count, one of `COLUMN_MARKS`. */
  columns: number;
  onColumns: (columns: number) => void;
  /** The rows' height, one of `ROW_HEIGHTS`. */
  rowHeight: number;
  onRowHeight: (height: number) => void;
  /** On the button, for its place in the page's toolbar. */
  className?: string | undefined;
}

/**
 * Live's "Display settings" of a gallery with layouts (`display-grid-settings`, spec
 * library-rows; captures gaps/24 and 30): "Layout", the grid or the rows as two pill radio cards,
 * then "Columns" for the grid, held to live's 2, 4, 6, 8 and 10 on a scale to 12, or "Row
 * height" for the rows, held to Small, Medium and Large. The page keeps the choices in prefs.
 */
export function DisplaySettings(props: DisplaySettingsProps) {
  const { layout, onLayout, columns, onColumns, rowHeight, onRowHeight, className } = props;
  return (
    <Popover position="bottom-end" shadow="md">
      <Popover.Target>
        <ActionIcon variant="text" size="lg" aria-label="Display settings" {...(className === undefined ? {} : { className })}>
          <Settings2 size={20} strokeWidth={1.5} aria-hidden />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Text fw={600} size="sm" mb="sm">
          Display settings
        </Text>
        <div className={classes.content}>
          <div className={classes.section}>
            <Text className={classes.label}>Layout</Text>
            <Radio.Group value={layout} onChange={(value) => onLayout(value === "rows" ? "rows" : "masonry")} aria-label="Layout">
              <Group gap="xs">
                {LAYOUTS.map(({ value, label, Icon }) => (
                  <Radio.Card key={value} value={value} className={classes.option} aria-label={label}>
                    <Icon size={16} aria-hidden />
                  </Radio.Card>
                ))}
              </Group>
            </Radio.Group>
          </div>
          <div className={classes.settings}>
            {layout === "masonry" ? (
              <div className={classes.section}>
                <Text className={classes.label}>Columns</Text>
                <Slider
                  w={230}
                  max={12}
                  label={null}
                  restrictToMarks
                  value={columns}
                  marks={COLUMN_MARKS.map((count) => ({ value: count, label: String(count) }))}
                  onChange={onColumns}
                  thumbLabel="Columns"
                />
              </div>
            ) : (
              <div className={classes.section}>
                <Text className={classes.label}>Row height</Text>
                <Slider
                  w={230}
                  min={100}
                  max={300}
                  label={null}
                  restrictToMarks
                  marks={[...ROW_HEIGHTS]}
                  value={rowHeight}
                  onChange={onRowHeight}
                  thumbLabel="Row height"
                />
              </div>
            )}
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
